import React from 'react';
import { createPortal } from 'react-dom';
import { VehiculeConfig } from '../../../../shared/types/config';

interface VehiculeAddModalProps {
  show: boolean;
  onClose: () => void;
  vehiculeConfigs: VehiculeConfig[];
  loadingConfigs: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isCreating: boolean;
  onCreateVehicule: (config: VehiculeConfig) => void;
  createError: string | null;
  createSuccess: string | null;
}

const VehiculeAddModal: React.FC<VehiculeAddModalProps> = ({
  show,
  onClose,
  vehiculeConfigs,
  loadingConfigs,
  searchQuery,
  onSearchChange,
  isCreating,
  onCreateVehicule,
  createError,
  createSuccess,
}) => {
  if (!show) return null;

  const filteredConfigs = vehiculeConfigs.filter(config =>
    config.nom.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
    config.type.toLowerCase().includes(searchQuery.toLowerCase().trim())
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-zinc-900 rounded-lg shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-zinc-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-zinc-700 bg-zinc-800/50">
          <h2 className="text-2xl font-bold text-white">Ajouter un véhicule</h2>
          <button 
            onClick={onClose}
            className="text-zinc-400 hover:text-white transition-colors p-2 hover:bg-zinc-700 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {createError && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {createError}
            </div>
          )}
          {createSuccess && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
              {createSuccess}
            </div>
          )}
          
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un véhicule (nom ou type)..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-2.5 w-5 h-5 text-zinc-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(80vh-220px)]">
            {loadingConfigs ? (
              <div className="text-center py-12 text-zinc-400">
                Chargement des configurations...
              </div>
            ) : filteredConfigs.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <p className="text-lg mb-2">
                  {searchQuery ? 'Aucun résultat trouvé' : 'Aucune configuration disponible'}
                </p>
                <p className="text-sm">
                  {searchQuery 
                    ? 'Essayez de modifier votre recherche'
                    : 'Aucune configuration de véhicule trouvée pour ce service'}
                </p>
              </div>
            ) : (
              <>
                <div className="text-sm text-zinc-400 mb-3">
                  {filteredConfigs.length} configuration{filteredConfigs.length > 1 ? 's' : ''} trouvée{filteredConfigs.length > 1 ? 's' : ''}
                  {searchQuery && ` pour "${searchQuery}"`}
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {filteredConfigs.map((config) => (
                    <div key={config.id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 hover:border-sky-500 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-white">{config.nom}</h4>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-xs">
                              {config.type}
                            </span>
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                              Prix: {config.prix}€
                            </span>
                          </div>
                        </div>
                        <button 
                          onClick={() => onCreateVehicule(config)}
                          disabled={isCreating}
                          className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-600 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          {isCreating ? 'Création...' : 'Ajouter'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VehiculeAddModal;


